import { useMemo, useState } from "react";
import { Check, Clock3, History, SearchCheck } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const normalizar = (valor = "") =>
  String(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();

const formatarDataHora = (valor) => {
  if (!valor) return null;
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return null;
  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const lerHistorico = (cliente) =>
  (cliente?.historico_tags || cliente?.historico_esteira || [])
    .map((item) => ({
      nome:
        item.tag_nova_nome ||
        item.esteira_nova_nome ||
        item.esteira_nova ||
        item.tag_nome ||
        "Movimentação",
      data: item.data || item.created_at || item.entrada,
      responsavel: item.operador_nome || item.usuario_nome || item.responsavel,
    }))
    .filter((item) => item.data);

const localizarPrimeiro = (historico, termos) =>
  historico.find((item) => {
    const nome = normalizar(item.nome);
    return termos.some((termo) => nome.includes(termo));
  });

const TERMOS_RESULTADO = [
  "COM VALORES",
  "SEM VALORES",
  "MARGEM NEGATIVA",
  "ACEITOU A OFERTA",
  "SEM INTERESSE",
];

export default function JornadaCreditoCliente({ cliente }) {
  const [historicoAberto, setHistoricoAberto] = useState(false);

  const jornada = useMemo(() => {
    const historico = lerHistorico(cliente).sort(
      (a, b) => new Date(a.data) - new Date(b.data)
    );
    const atual = normalizar(cliente?.tag_gestao_nome || cliente?.esteira_atual);
    const qualificacao = localizarPrimeiro(historico, ["QUALIFIC"]);
    const simulacao = localizarPrimeiro(historico, ["SIMULACAO"]);
    const resultado = [...historico]
      .reverse()
      .find((item) => TERMOS_RESULTADO.some((termo) => normalizar(item.nome).includes(termo)));
    const consulta = cliente?.ultima_consulta_vanguard || {};
    const dataVanguard = consulta.consultado_em;
    const passouQualificacao = Boolean(
      qualificacao || dataVanguard || simulacao || resultado || atual.includes("SIMULACAO")
    );
    const chegouSimulacao = Boolean(simulacao || resultado || atual.includes("SIMULACAO"));

    const etapas = [
      {
        nome: "Qualificação",
        data: qualificacao?.data,
        concluida: passouQualificacao && !atual.includes("QUALIFIC"),
        ativa: atual.includes("QUALIFIC"),
      },
      {
        nome: "Vanguard atualizado",
        data: dataVanguard,
        concluida: Boolean(dataVanguard),
        ativa: passouQualificacao && !dataVanguard && !resultado,
      },
      {
        nome: "Análise e simulação",
        data: simulacao?.data,
        concluida: chegouSimulacao && Boolean(resultado),
        ativa: atual.includes("SIMULACAO") && !resultado,
      },
      {
        nome: resultado?.nome || "Resultado",
        data: resultado?.data,
        concluida: Boolean(resultado),
        ativa: false,
      },
    ];

    const eventos = historico
      .filter((item) => {
        const nome = normalizar(item.nome);
        return (
          nome.includes("QUALIFIC") ||
          nome.includes("SIMULACAO") ||
          TERMOS_RESULTADO.some((termo) => nome.includes(termo))
        );
      })
      .map((item) => ({ ...item, tipo: "Etapa do funil" }));

    if (dataVanguard) {
      eventos.push({
        nome: "Cadastro atualizado pelo Vanguard",
        data: dataVanguard,
        responsavel: "Robô Vanguard",
        tipo: consulta.tipo_extrato || "Consulta Vanguard",
      });
    }

    eventos.sort((a, b) => new Date(b.data) - new Date(a.data));
    return { etapas, eventos, dataVanguard };
  }, [cliente]);

  return (
    <div className="min-w-0 flex-1" data-testid="jornada-credito-cliente">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <SearchCheck className="h-4 w-4 text-blue-600" />
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            Jornada de Crédito
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs text-blue-700"
          onClick={() => setHistoricoAberto(true)}
          data-testid="btn-historico-jornada-credito"
        >
          <History className="h-3.5 w-3.5" />
          Ver histórico
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {jornada.etapas.map((etapa, index) => {
          const estado = etapa.concluida ? "concluida" : etapa.ativa ? "ativa" : "pendente";
          return (
            <div
              key={`${etapa.nome}-${index}`}
              className={cn(
                "relative rounded-lg border px-3 py-2",
                estado === "concluida" && "border-emerald-200 bg-emerald-50",
                estado === "ativa" && "border-blue-300 bg-blue-50 ring-1 ring-blue-200",
                estado === "pendente" && "border-slate-200 bg-white"
              )}
              data-testid={`jornada-etapa-${index}`}
            >
              <div className="flex items-start gap-2">
                <span
                  className={cn(
                    "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
                    estado === "concluida" && "bg-emerald-600 text-white",
                    estado === "ativa" && "bg-blue-600 text-white",
                    estado === "pendente" && "border border-slate-300 bg-slate-100 text-slate-400"
                  )}
                >
                  {estado === "concluida" ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <span className="text-[10px] font-bold">{index + 1}</span>
                  )}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-slate-800">{etapa.nome}</p>
                  <p className="mt-0.5 truncate text-[10px] text-slate-500">
                    {formatarDataHora(etapa.data) ||
                      (estado === "ativa" ? "Em andamento" : "Aguardando")}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={historicoAberto} onOpenChange={setHistoricoAberto}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-600" />
              Histórico da Jornada de Crédito
            </DialogTitle>
            <DialogDescription>{cliente?.nome}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2" data-testid="historico-jornada-credito">
            {jornada.eventos.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-slate-500">
                Nenhuma análise de crédito registrada para este cliente.
              </div>
            ) : (
              jornada.eventos.map((evento, index) => (
                <div key={`${evento.nome}-${evento.data}-${index}`} className="flex gap-3 rounded-lg border p-3">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-700">
                    <Clock3 className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-semibold text-slate-800">{evento.nome}</p>
                      <Badge variant="outline" className="text-[10px]">{evento.tipo}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {formatarDataHora(evento.data)}
                      {evento.responsavel ? ` · ${evento.responsavel}` : ""}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
